import {
  toUtf8,
  toBase64,
  MsgExecuteContractCompat,
  WasmContractQueryResponse,
  MsgInstantSpotMarketLaunch
} from '@injectivelabs/sdk-ts'
import { wasmApi } from '@shared/Service'
import { injToken } from '@shared/data/token'
import { msgBroadcaster } from '@shared/WalletService'
import { BigNumberInBase } from '@injectivelabs/utils'
import { sharedToBalanceInWei } from '@shared/utils/formatter'
import {
  IS_DEVNET,
  CPMM_CONTRACT_CODE,
  INJ_PYTH_PRICE_FEED_ID,
  LAUNCHPAD_DEFAULT_MAX_CAP,
  BASIS_POINT_DECIMAL_PLACES,
  MITO_MASTER_CONTRACT_ADDRESS,
  MASTER_CONTRACT_OWNER_ADDRESS,
  LAUNCHPAD_FACTORY_CONTRACT_ADDRESS
} from '@/app/utils/constant'
import { delayPromiseCall } from '@/app/client/utils/async'
import { formatVestingSchedule } from '@/app/client/utils/vaultConfig/formatter'
import {
  MarketField,
  CPMMOrderType,
  IDOMarketForm,
  VaultConfigField,
  IDOProjectForm,
  IDOProjectField,
  WeightedWhitelist
} from '@/types'

export const queryUserHasOneTimeAllowance = async ({
  address,
  codeId,
  masterContractAddress
}: {
  address: string
  codeId: string
  masterContractAddress: string
}) => {
  const response = (await wasmApi.fetchSmartContractState(
    masterContractAddress,
    toBase64({
      has_one_time_allowance: { code_id: Number(codeId), user_address: address }
    })
  )) as WasmContractQueryResponse

  return JSON.parse(toUtf8(response.data)) as boolean
}

export const instantiateIDO = async (formValues: IDOProjectForm) => {
  const paramStore = useParamStore()
  const walletStore = useWalletStore()
  const launchpadStore = useLaunchpadStore()
  const sharedWalletStore = useSharedWalletStore()

  if (
    !sharedWalletStore.isUserConnected ||
    !sharedWalletStore.defaultSubaccountId
  ) {
    return
  }

  await walletStore.validateAndQueue()

  const ORACLE_STALE_TIME = IS_DEVNET
    ? 315360000 // 10 years devnet oracle price doesn't update
    : 30 * 60 // 30 mins

  const hardCap = sharedToBalanceInWei({
    value: formValues[IDOProjectField.HardCap] || '0',
    decimalPlaces: formValues[IDOProjectField.QuoteDecimals]
  }).toFixed()

  const START_IN_SECONDS = 60

  const msg = {
    hard_cap: hardCap,
    project_owner: sharedWalletStore.injectiveAddress,
    target_usd_subscription_incl_vault:
      formValues[IDOProjectField.TargetRaiseAmount],
    quote_price_set_once_before_start_in_seconds: START_IN_SECONDS,
    use_whitelist: formValues[IDOProjectField.UseWhitelist],
    project_description: formValues[IDOProjectField.Website],
    project_token_denom: formValues[IDOProjectField.ProjectTokenDenom],
    quote_pyth_pricefeed_id: INJ_PYTH_PRICE_FEED_ID,
    oracle_stale_time: ORACLE_STALE_TIME
  }

  const cpmmLaunchConfig = {
    code_id: CPMM_CONTRACT_CODE,
    msg: {
      config_owner: MASTER_CONTRACT_OWNER_ADDRESS,
      master_address: MITO_MASTER_CONTRACT_ADDRESS,
      vault_upgrade_admin: MASTER_CONTRACT_OWNER_ADDRESS,
      market_id: formValues[VaultConfigField.MarketId],
      order_density: 35,
      pricing_strategy: {
        SmoothingPricingWithRelativePriceRange: {
          bid_range: '0.8',
          ask_range: '0.8'
        }
      },
      base_decimals: Number(formValues[IDOProjectField.BaseDecimals]),
      quote_decimals: Number(formValues[IDOProjectField.QuoteDecimals]),
      notional_value_cap: sharedToBalanceInWei({
        value: 100_000_000
      }).toFixed(),
      max_invariant_sensitivity_bps: sharedToBalanceInWei({
        value: 0.05,
        decimalPlaces: BASIS_POINT_DECIMAL_PLACES
      }).toFixed(),
      max_price_sensitivity_bps: sharedToBalanceInWei({
        value: 0.05,
        decimalPlaces: BASIS_POINT_DECIMAL_PLACES
      }).toFixed(),
      fee_bps: sharedToBalanceInWei({
        value: 1,
        decimalPlaces: BASIS_POINT_DECIMAL_PLACES
      }).toNumber(),
      order_type: CPMMOrderType.Vanilla
    }
  }

  const launchFee = new BigNumberInBase(launchpadStore.instantiationFee)
    .plus(paramStore.permissionlessVaultRegistrationFee)
    .plus(paramStore.lpTokenCreationFee)

  const message = MsgExecuteContractCompat.fromJSON({
    sender: sharedWalletStore.injectiveAddress,
    funds: [
      {
        denom: formValues[IDOProjectField.ProjectTokenDenom], // base
        amount: sharedToBalanceInWei({
          value: formValues[IDOProjectField.ProjectTokenAmount],
          decimalPlaces: formValues[IDOProjectField.BaseDecimals]
        }).toFixed()
      },
      {
        denom: injToken.denom,
        amount: launchFee.toFixed()
      }
    ],
    contractAddress: LAUNCHPAD_FACTORY_CONTRACT_ADDRESS,
    exec: {
      action: 'DeployLaunchpad',
      msg: {
        ...msg,
        ido_timestamps: {
          start: `${formValues[IDOProjectField.StartTimestamp] * 1000000}`,
          end: `${formValues[IDOProjectField.EndTimestamp] * 1000000}`
        },
        vesting_config: formatVestingSchedule(formValues),
        amm_launch_config: cpmmLaunchConfig,
        // remove staking requirement
        staked_to_subscription_override: {
          0: sharedToBalanceInWei({
            value: LAUNCHPAD_DEFAULT_MAX_CAP
          }).toFixed()
        }
      }
    }
  })

  const tx = await msgBroadcaster.broadcastV2({
    injectiveAddress: sharedWalletStore.injectiveAddress,
    msgs: message
  })

  return tx
}

export const launchSpotMarket = async (formValues: IDOMarketForm) => {
  const spotStore = useSpotStore()
  const tokenStore = useTokenStore()
  const walletStore = useWalletStore()
  const sharedWalletStore = useSharedWalletStore()

  if (!sharedWalletStore.isUserConnected) {
    return
  }

  const baseToken = tokenStore.tokenByDenomOrSymbol(
    formValues[MarketField.BaseDenom]
  )

  const quoteToken = tokenStore.tokenByDenomOrSymbol(
    formValues[MarketField.QuoteDenom]
  )

  if (!baseToken || !quoteToken) {
    return
  }

  await walletStore.validateAndQueue()

  const message = MsgInstantSpotMarketLaunch.fromJSON({
    market: {
      ticker: formValues[MarketField.Ticker],
      sender: sharedWalletStore.injectiveAddress,
      baseDenom: formValues[MarketField.BaseDenom],
      quoteDenom: formValues[MarketField.QuoteDenom],
      baseDecimals: baseToken.decimals,
      quoteDecimals: quoteToken.decimals,
      minPriceTickSize: formValues[MarketField.MinPriceTickSize],
      minQuantityTickSize: formValues[MarketField.MinQuantityTickSize],
      minNotional: sharedToBalanceInWei({
        value: formValues[MarketField.MinNotional],
        decimalPlaces: quoteToken.decimals
      }).toFixed()
    },
    proposer: sharedWalletStore.injectiveAddress
  })

  await msgBroadcaster.broadcastV2({
    injectiveAddress: sharedWalletStore.injectiveAddress,
    msgs: message
  })

  await spotStore.fetchMarkets()
}

export const finalizeIDO = async ({
  contractAddress
}: {
  contractAddress: string
}) => {
  const walletStore = useWalletStore()
  const launchpadStore = useLaunchpadStore()
  const sharedWalletStore = useSharedWalletStore()

  if (
    !sharedWalletStore.isUserConnected ||
    !sharedWalletStore.defaultSubaccountId
  ) {
    return
  }

  await walletStore.validateAndQueue()

  const message = MsgExecuteContractCompat.fromJSON({
    contractAddress,
    sender: sharedWalletStore.injectiveAddress,
    exec: {
      action: 'Finalize',
      msg: {}
    }
  })

  const tx = await msgBroadcaster.broadcastV2({
    injectiveAddress: sharedWalletStore.injectiveAddress,
    msgs: message
  })

  await delayPromiseCall(
    () =>
      Promise.all([
        launchpadStore.fetchIDO(contractAddress),
        launchpadStore.fetchClaimReference(contractAddress)
      ]),
    3000
  )

  return tx
}

export const addWhitelistedAccounts = async ({
  addresses,
  contractAddress
}: {
  contractAddress: string
  addresses: WeightedWhitelist[]
}) => {
  const walletStore = useWalletStore()
  const launchpadStore = useLaunchpadStore()
  const sharedWalletStore = useSharedWalletStore()

  if (
    !sharedWalletStore.isUserConnected ||
    !sharedWalletStore.defaultSubaccountId
  ) {
    return
  }

  await walletStore.validateAndQueue()

  const message = MsgExecuteContractCompat.fromJSON({
    contractAddress,
    sender: sharedWalletStore.injectiveAddress,
    exec: {
      action: 'AddAddressesToWhitelist',
      msg: {
        addresses: addresses.map(({ address, multiplier }) => ({
          address,
          weight: multiplier
        }))
      }
    }
  })

  const tx = await msgBroadcaster.broadcastV2({
    injectiveAddress: sharedWalletStore.injectiveAddress,
    msgs: message
  })

  await delayPromiseCall(
    async () =>
      await launchpadStore.fetchIDOWhitelistedAccounts(contractAddress),
    10 * 1000
  )

  return tx
}

export const removeWhitelistedAccounts = async ({
  addresses,
  contractAddress,
  shouldRevokeSubscription
}: {
  contractAddress: string
  addresses: WeightedWhitelist[]
  shouldRevokeSubscription: boolean
}) => {
  const walletStore = useWalletStore()
  const launchpadStore = useLaunchpadStore()
  const sharedWalletStore = useSharedWalletStore()

  if (
    !sharedWalletStore.isUserConnected ||
    !sharedWalletStore.defaultSubaccountId
  ) {
    return
  }

  await walletStore.validateAndQueue()

  const message = MsgExecuteContractCompat.fromJSON({
    contractAddress,
    sender: sharedWalletStore.injectiveAddress,
    exec: {
      action: 'RemoveAddressesFromWhitelist',
      msg: {
        should_revoke_subscription: shouldRevokeSubscription,
        addresses: addresses.map(({ address }) => address)
      }
    }
  })

  const tx = await msgBroadcaster.broadcastV2({
    injectiveAddress: sharedWalletStore.injectiveAddress,
    msgs: message
  })

  await delayPromiseCall(
    async () =>
      await launchpadStore.fetchIDOWhitelistedAccounts(contractAddress),
    10 * 1000
  )

  return tx
}

export const updateProjectDescription = async ({
  description,
  contractAddress
}: {
  description: string
  contractAddress: string
}) => {
  const walletStore = useWalletStore()
  const launchpadStore = useLaunchpadStore()
  const sharedWalletStore = useSharedWalletStore()

  if (
    !sharedWalletStore.isUserConnected ||
    !sharedWalletStore.defaultSubaccountId
  ) {
    return
  }

  await walletStore.validateAndQueue()

  const message = MsgExecuteContractCompat.fromJSON({
    contractAddress,
    sender: sharedWalletStore.injectiveAddress,
    exec: {
      action: 'UpdateProjectDescription',
      msg: {
        new_project_description: description
      }
    }
  })

  const tx = await msgBroadcaster.broadcastV2({
    injectiveAddress: sharedWalletStore.injectiveAddress,
    msgs: message
  })

  await delayPromiseCall(
    async () => await launchpadStore.fetchIDO(contractAddress),
    10 * 1000
  )

  return tx
}

export const grantOneTimeAllowance = async ({
  codeId,
  address,
  masterContractAddress
}: {
  codeId: string
  address: string
  masterContractAddress: string
}) => {
  const walletStore = useWalletStore()
  const sharedWalletStore = useSharedWalletStore()

  if (
    !sharedWalletStore.isUserConnected ||
    !sharedWalletStore.defaultSubaccountId
  ) {
    return
  }

  await walletStore.validateAndQueue()

  const message = MsgExecuteContractCompat.fromJSON({
    contractAddress: masterContractAddress,
    sender: sharedWalletStore.injectiveAddress,
    exec: {
      action: 'add_one_time_allowed_vault_registration_address',
      msg: {
        address,
        code_id: Number(codeId)
      }
    }
  })

  const tx = await msgBroadcaster.broadcastV2({
    injectiveAddress: sharedWalletStore.injectiveAddress,
    msgs: message
  })

  return tx
}
